import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, PenLine, ShieldCheck, X, XCircle } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PlatformAgreementCard } from "@/components/contracts/PlatformAgreementCard";
import { SignatureBlock } from "@/components/contracts/SignatureBlock";
import {
  cancelContract,
  getContract,
  listContracts,
  signContract,
} from "@/lib/contracts.functions";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/contracts")({
  head: () =>
    buildSeo({
      path: "/contracts",
      title: "Contracts — Reality Twin",
      description:
        "Sign and manage your service distribution agreement and the contracts you conclude with other companies in the network.",
      noindex: true,
    }),
  component: ContractsPage,
});

const STATUS_TONE: Record<string, string> = {
  signed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  sent: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  draft: "border-border text-muted-foreground",
  declined: "border-red-500/30 bg-red-500/10 text-red-400",
  cancelled: "border-border text-muted-foreground",
};

function ContractsPage() {
  const t = useHydratedTranslation();
  const fetchList = useServerFn(listContracts);
  const [data, setData] = useState<any>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchList()
      .then(setData)
      .catch((e: any) => toast.error(e?.message ?? "Failed"));
  }, [fetchList]);

  useEffect(() => {
    load();
  }, [load]);

  const engagements = (data?.contracts ?? []).filter((c: any) => c.kind === "service_engagement");

  return (
    <DashboardShell
      title={t("app.pages.contracts.title", { defaultValue: "Contracts" })}
      subtitle={t("app.pages.contracts.subtitle", {
        defaultValue: "Your distribution agreement and every contract you sign in the network.",
      })}
    >
      <div className="space-y-5 max-w-3xl">
        <PlatformAgreementCard onSigned={load} />

        <section>
          <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-muted-foreground font-mono">
            <FileSignature className="h-3.5 w-3.5" />
            {t("app.pages.contracts.engagements", { defaultValue: "Service contracts" })}
          </div>

          {!data ? (
            <div className="space-y-2">
              {[0, 1].map((n) => (
                <div
                  key={n}
                  className="h-16 rounded-2xl border border-border bg-white/[0.02] animate-pulse"
                />
              ))}
            </div>
          ) : engagements.length === 0 ? (
            <div className="glass-strong rounded-2xl p-6 text-sm text-muted-foreground">
              {t("app.pages.contracts.empty", {
                defaultValue:
                  "No service contracts yet. Once you agree terms with another company, the contract appears here for both sides to sign.",
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-border overflow-hidden divide-y divide-border">
              {engagements.map((c: any) => (
                <button
                  key={c.id}
                  onClick={() => setOpenId(c.id)}
                  className="w-full text-left p-4 bg-white/[0.02] hover:bg-white/5 transition flex items-start gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.counterpartName ?? "—"} ·{" "}
                      {c.myRole === "provider"
                        ? t("app.pages.contracts.asProvider", { defaultValue: "you deliver" })
                        : t("app.pages.contracts.asCustomer", { defaultValue: "you receive" })}{" "}
                      · {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${STATUS_TONE[c.status] ?? STATUS_TONE.draft}`}
                    >
                      {t(`app.pages.contracts.st_${c.status}`, { defaultValue: c.status })}
                    </span>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {t("app.pages.contracts.sigCount", {
                        defaultValue: "{{n}}/2 signed",
                        n: c.signatures.length,
                      })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {openId && (
        <ContractDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            load();
          }}
        />
      )}
    </DashboardShell>
  );
}

function ContractDrawer({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useHydratedTranslation();
  const fetchOne = useServerFn(getContract);
  const sign = useServerFn(signContract);
  const cancel = useServerFn(cancelContract);
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchOne({ data: { id } })
      .then(setD)
      .catch((e: any) => toast.error(e?.message ?? "Failed"));
  }, [fetchOne, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const c = d?.contract;
  const mySignature = (d?.signatures ?? []).find((s: any) => s.role === d?.myRole);
  const canSign = c && !mySignature && c.status !== "cancelled" && c.status !== "declined";

  async function doSign(name: string) {
    setBusy(true);
    try {
      await sign({ data: { id, signerName: name, bodyHash: c.body_hash, accepted: true } });
      toast.success(t("app.pages.contracts.signed", { defaultValue: "Contract signed" }));
      load();
      onChanged();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        aria-label={t("app.pages.contracts.close", { defaultValue: "Close" })}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
      />
      <div className="relative h-full w-full sm:max-w-2xl overflow-y-auto border-l border-border bg-background shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-border bg-background/95 backdrop-blur px-4 sm:px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
              {t("app.pages.contracts.contract", { defaultValue: "Contract" })}
            </div>
            <div className="mt-0.5 font-display text-lg font-semibold truncate">
              {c?.title ?? "…"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-full border border-border p-2 hover:bg-white/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!c ? (
          <div className="p-6 text-sm text-muted-foreground">{t("app.pages.common.loading")}</div>
        ) : (
          <div className="p-4 sm:p-6 space-y-4">
            <ContractBody body={c.body} hash={c.body_hash} version={c.version} />

            <SignatureList signatures={d.signatures} />

            {canSign ? (
              <SignatureBlock busy={busy} onSign={doSign} />
            ) : mySignature ? (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-400 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                {t("app.pages.contracts.youSigned", {
                  defaultValue: "You signed this contract on {{date}}.",
                  date: new Date(mySignature.signed_at).toLocaleString(),
                })}
              </div>
            ) : null}

            {d.myRole === "provider" && c.status !== "signed" && c.status !== "cancelled" && (
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await cancel({ data: { id } });
                    toast.success(
                      t("app.pages.contracts.withdrawn", { defaultValue: "Contract withdrawn" }),
                    );
                    load();
                    onChanged();
                  } catch (e: any) {
                    toast.error(e?.message ?? "Failed");
                  } finally {
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-400 disabled:opacity-50"
              >
                <XCircle className="h-3.5 w-3.5" />
                {t("app.pages.contracts.withdraw", { defaultValue: "Withdraw this contract" })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ContractBody({
  body,
  hash,
  version,
}: {
  body: string;
  hash?: string | null;
  version?: string | null;
}) {
  const t = useHydratedTranslation();
  return (
    <div>
      <pre className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl border border-border bg-white/[0.02] p-4 text-[12.5px] leading-relaxed font-sans">
        {body}
      </pre>
      {(hash || version) && (
        <div className="mt-1.5 text-[10.5px] text-muted-foreground font-mono break-all">
          {version && `v${version}`}
          {hash && ` · SHA-256 ${hash.slice(0, 16)}…`}
          <span className="ml-1 font-sans">
            {t("app.pages.contracts.hashNote", {
              defaultValue: "— the fingerprint your signature is bound to",
            })}
          </span>
        </div>
      )}
    </div>
  );
}

function SignatureList({ signatures }: { signatures: any[] }) {
  const t = useHydratedTranslation();
  if (!signatures.length) return null;
  return (
    <div className="rounded-2xl border border-border divide-y divide-border">
      {signatures.map((s) => (
        <div key={s.id} className="p-3.5">
          <div className="flex items-center gap-2 text-sm">
            <PenLine className="h-3.5 w-3.5 text-[oklch(0.82_0.18_168)]" />
            <span className="font-medium">{s.signer_name}</span>
            <span className="text-xs text-muted-foreground">
              {t(`app.pages.contracts.role_${s.role}`, { defaultValue: s.role })}
            </span>
          </div>
          <div className="mt-1 text-[10.5px] text-muted-foreground font-mono break-all">
            {new Date(s.signed_at).toLocaleString()}
            {s.signer_email ? ` · ${s.signer_email}` : ""}
            {s.ip ? ` · ${s.ip}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}
